/* eslint-disable require-jsdoc */
import React, {useEffect, useState, useRef, forwardRef, useImperativeHandle, useMemo} from 'react';
import {
    TextField, FormControl, Select, MenuItem, Checkbox, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper, TableFooter, IconButton, FormGroup, FormControlLabel, InputAdornment,
} from '@mui/material';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import {bgColorDisabled, ezprOrange} from '../../_helpers';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {TablePaginationWithPageSelector} from '../../TablePaginationWithPageSelector.jsx';
import {requestStores, useApplicationStore} from '../../ListOfPREditors.brick.jsx';
import {SuppliersTable} from '../../pr_editor/dialogs/search_supplier_components/SuppliersTable.jsx';
import {debounce} from 'lodash';
import {EZPRSelect} from '../../pr_editor/utils/EZPRSelect.jsx';
import {
    getConditionTypesLambda,
    getSuppliersByCodeOrNameLambda,
    getSuppliersLambda,
} from '../../../utils/functionSignatures';
import {MasterDataDropdown} from '../../pr_editor/utils/MasterDataDropdown.jsx';
import CustomPopup from '../CustomPopup.jsx';
import {formatCurrencySeparator, normalizeDecimalSeparator} from '../../../utils/number.js';
import {CurrencyController} from '../../../controllers/CurrencyController.js';
import {calculateTotalValueOfCondition} from '../../../utils/transportCostConditions.js';
import {NumberTextfieldSmall} from '../../pr_editor/utils/NumberTextfieldSmall.jsx';
import {FormatCurrencySeparatorTextfield} from '../../pr_editor/utils/FormatCurrencySeparatorTextfield.jsx';
import {LAMBDA_NAMES} from '../../../utils/constants';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../../pr_editor/utils/useFieldEditability.js';

/**
 * Form that lets the user select between different suppliers, or justify why none of the supplier match its needs
 *
 * @param {Object} props
 * @return {JSX.Element}
 * @constructor
 */
export const ConditionTypeContent = forwardRef((props, ref) => {
    const useRequestStore = requestStores.get(props.prTag);
    const vendorName = useRequestStore((state) => state.vendorName);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrg = useRequestStore((state) => state.purchaseOrganization);
    const materials = useRequestStore((state) => state.materials);
    const userId = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const EPRId = useRequestStore((state) => state.EPRId);
    const companyCodeCurrency = useRequestStore((state) => state.companyCodeCurrency);
    const deleteConditionTypeForAllMaterials = useRequestStore((state)=> state.deleteConditionTypeForAllMaterials);
    const addConditionTypeForAllMaterials = useRequestStore((state)=> state.addConditionTypeForAllMaterials);
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const separator = useApplicationStore((state) => state.userCurrencySeparator);

    const getSuppliers = props.context.get(LAMBDA_NAMES.GET_SUPPLIERS);
    const getSuppliersByCodeOrName = props.context.get(LAMBDA_NAMES.GET_SUPPLIERS_CODE_OR_NAME);
    const getConditionTypes = props.context.get(LAMBDA_NAMES.GET_CONDITION_TYPES);
    const getMasterData = props.context.get(LAMBDA_NAMES.GET_MASTER_DATA);
    const minInputValue = 0;

    const [localizedOnly, setLocalizedOnly] = useState(true);
    const [supplierSearchValue, setSupplierSearchValue] = useState('');
    const [supplierSearchType, setSupplierSearchType] = useState('name1');

    const [conditions, setConditions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [conditionTypes, setConditionTypes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState({supplierCode: vendorCode, supplierName: vendorName});
    const initialSupplier = useRef({supplierCode: vendorCode, supplierName: vendorName});
    const [useCurrentSupplier, setUseCurrentSupplier] = useState(true);
    const [showSelectSupplier, setShowSelectSupplier] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageNum, setPageNum] = useState([]);
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const pageSize = rowsPerPage === -1 ? conditions.length : (conditions.length) / rowsPerPage;
    const [numberOfResults, setNumberOfResults] = useState(0);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const selectedIdForDeletion = useRef(null);

    const materialCurrency = useRef(null);
    const materialUnit = useRef(null);

    const subtotalDecimals = materialCurrency.current?.value === 'USDN' ? 5 : 2;

    const [suppliersPerCondition, setSuppliersPerCondition] = useState({});
    const [isPerformingQueryPerCondition, setIsPerformingQueryPerCondition] = useState({});

    /**
     * @param {string} queryValue
     * @param {string} queryType
     * @param {boolean} loc
     */
    const querySuppliers = async (queryValue, queryType, loc) => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        // query only if search value is long enough
        if ((queryType === 'land1' && queryValue.length < 2) || (queryType !== 'land1' && queryValue.length < 3)) {
            return;
        }

        // start a query
        setIsPerformingQuery(true);

        try {
            const companyCodeIn = loc && queryType !== 'bukrs' ? companyCode.value : null;
            const purchaseOrgIn = loc && queryType !== 'ekorg' ? purchaseOrg.value : null;

            const [_, results] = await getSuppliersLambda(
                props.context,
                getSuppliers,
                queryValue,
                queryType,
                companyCodeIn,
                purchaseOrgIn,
            );

            const receivedSuppliers = JSON.parse(results);
            setSuppliers(receivedSuppliers);
            setNumberOfResults(receivedSuppliers.length);
            setShowTable(true);
        } catch (error) {
            console.error(error);
        } finally {
            setIsPerformingQuery(false);
        }
    };

    /**
     * @param {string} searchValue
     * @param {string} idx
     */
    const querySuppliersCodeOrName = async (searchValue, idx) => {
        // avoid performing several queries simultaneously
        if (!searchValue || searchValue.length < 3) {
            setIsPerformingQueryPerCondition((prev) => ({...prev, [idx]: false}));
            setSuppliersPerCondition((prev) => ({...prev, [idx]: []}));
            return;
        }

        // start a query
        setIsPerformingQueryPerCondition((prev) => ({...prev, [idx]: true}));

        // Parse the searchValue to determine if it's a code, name, or both
        const trimmedSearchValue = searchValue.trim();
        let codeInput = null;
        let nameInput = null;

        // Split the input based on spaces and hyphens
        const tokens = trimmedSearchValue.split(/[\s-]+/);
        if (tokens.length > 1 && /^\d+$/.test(tokens[0])) {
            // First token is digits, treat as code
            codeInput = tokens[0];
            nameInput = tokens.slice(1).join(' ');
        } else if (/^\d+$/.test(trimmedSearchValue)) {
            // Entire input is digits
            codeInput = trimmedSearchValue;
        } else {
            // Treat entire input as name
            nameInput = trimmedSearchValue;
        }

        try {
            const [_, resultsJSON] = await getSuppliersByCodeOrNameLambda(
                props.context,
                getSuppliersByCodeOrName,
                codeInput,
                nameInput,
                companyCode.value,
                purchaseOrg.value,
            );

            const receivedSuppliers = JSON.parse(resultsJSON);

            const filteredSuppliers = receivedSuppliers.filter((supplier) => {
                let isDisabled = companyCode.value !== supplier['bukrs'] || purchaseOrg.value !== supplier['ekorg'];

                if (isDisabled && companyCode.value.startsWith('DE') && purchaseOrg.value === supplier['ekorg']) {
                    isDisabled = false;
                }

                return !(localizedOnly && isDisabled);
            });

            const formattedSuppliers = filteredSuppliers.map((supplier) => ({
                value: `${supplier.lifnr} - ${supplier.name1}`,
                label: `${supplier.lifnr} - ${supplier.name1}`,
            }));

            setSuppliersPerCondition((prev) => ({...prev, [idx]: formattedSuppliers}));
            setIsPerformingQueryPerCondition((prev) => ({...prev, [idx]: false}));
        } catch (error) {
            console.error(error);

            setSuppliersPerCondition((prev) => ({...prev, [idx]: []}));
            setIsPerformingQueryPerCondition((prev) => ({...prev, [idx]: false}));
        }
    };

    const queryConditionTypes = () => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        // start a query
        setIsPerformingQuery(true);
        function doConditionTypeCall() {
            getConditionTypesLambda(
                props.context,
                getConditionTypes,
                purchaseOrg.value,
                companyCode.value,
                userId,
                (result) => {
                    const receivedConditionTypes = JSON.parse(result);
                    const extractedConditionTypes = receivedConditionTypes.length > 0 ? receivedConditionTypes.map((item)=>{
                        return {
                            value: item.Cond_Type,
                            label: `${item.Cond_Type} - ${item.VTEXT}`,
                        };
                    }) : [{
                        value: '',
                        label: 'No options',
                    }];
                    setConditionTypes(extractedConditionTypes);
                    setIsPerformingQuery(false);
                    setLoading(false);
                },
                () => {
                    setConditionTypes([]);
                    setIsPerformingQuery(false);
                    setLoading(false);
                });
        }
        doConditionTypeCall();
    };

    const debouncedQuery = useMemo(() => debounce(querySuppliers, 800), []);
    const debouncedCodeOrNameQuery = useMemo(() => debounce(querySuppliersCodeOrName, 800), []);

    const material = (props.materialId !== null && props.materialId !== undefined) ? materials.filter((item)=> item.id === props.materialId)?.[0] : (props.selectedMaterialIds.length === 1 ? materials.filter((item)=> item.id === props.selectedMaterialIds[0])?.[0] : (materials.length === 1 ? materials[0] : null));
    // useEffect to set the conditions that are saved in the material. It means we selected only one
    useEffect(()=>{
        if (props.materialId !== null && props.materialId !== undefined && materials) {
            const tmpMaterial = materials.filter((item)=> item.id === props.materialId)?.[0];
            const tmpConditions = tmpMaterial?.transportCostConditions || [];
            if (tmpConditions.length > 0) {
                initialSupplier.current = {...tmpConditions[0].conditionSupplier};
                setSelectedSupplier({...tmpConditions[0].conditionSupplier});
            }
            setConditions([...tmpConditions]);
        }
    }, [props.materialId, materials]);

    useEffect(()=>{
        queryConditionTypes();
        materialCurrency.current = material?.currency || materials?.[0]?.currency || '';
        materialUnit.current = material?.unit || material?.baseUnit || materials?.[0]?.unit || materials?.[0]?.baseUnit;
    }, []);

    const handleClickOnDelete = (conditionIndex)=>{
        setShowDeleteConfirmation(true);
        selectedIdForDeletion.current = conditionIndex;
    };

    const handleClickOnSave = ()=>{
        // first filter the conditions to see that everything has values
        const tmpNewConditions = conditions.filter((item) => item.amount !== '' || item.percentage !== '' || item.perUnit !== '');
        setConditions(tmpNewConditions);

        if ( props.selectedMaterialIds.length > 1 && props.materialId) {
            // it means that we are in the edit mode with selection made
            // retrieve the difference between the conditions saved in the material and the new one
            let changedConditions = [];
            const materialConditions = material.transportCostConditions;
            if (materialConditions.length> 0 ) {
                tmpNewConditions.forEach((condition) => {
                    let conditionFound = false;
                    materialConditions.forEach((materialCondition) => {
                        if (!conditionFound && materialCondition.conditionType.value === condition.conditionType.value) {
                            // check if a value has changed
                            conditionFound = true;
                            const enabledFields = getEnabledFields(condition.conditionType);
                            if ( enabledFields.amount && materialCondition.amount !== condition.amount ||
                                enabledFields.percentage && materialCondition.percentage !== condition.percentage ||
                                enabledFields.perUnit && (materialCondition.perUnit !== condition.perUnit || materialCondition.amount !== condition.amount)) {
                                changedConditions.push({...condition});
                            }
                        }
                    });
                    if ( !conditionFound) {
                        changedConditions.push({...condition});
                    }
                });
            } else {
                changedConditions = [...tmpNewConditions];
            }
            changedConditions.forEach((changedCondition) => {
                addConditionTypeForAllMaterials(changedCondition, props.selectedMaterialIds );
            });
        }
    };

    const handleConditionTypeChanges = (evt, idx)=>{
        const selectedCondition = conditionTypes.filter((item)=> item.value === evt.value);
        updateAndResetCondition(idx, selectedCondition[0]);
    };

    const updateAndResetCondition = (idx, newValue) => {
        setConditions((prevState) => {
            const tmpConditions = structuredClone(prevState);
            tmpConditions.forEach((condition, index) => {
                if ( index == idx) {
                    condition.conditionType = {...newValue};
                    condition.amount = '';
                    condition.percentage = '';
                    condition.perUnit = '';
                    condition.conditionSupplier = selectedSupplier || initialSupplier.current || {supplierCode: vendorCode, supplierName: vendorName};
                    condition.conditionCurrency= materialCurrency.current;
                    condition.unit= materialUnit.current;
                    condition.eprId= EPRId;
                    condition.currencyExchangeRate= 1;
                }
            });
            return tmpConditions;
        });
    };

    const updateCondition = (idx, keyName, newValue) => {
        setConditions((prevState) => {
            const tmpConditions = structuredClone(prevState);
            tmpConditions.forEach((condition, index) => {
                if ( index == idx) {
                    condition[keyName] = newValue;
                }
            });
            return tmpConditions;
        });
    };

    const handleAmountChange = (evt, idx)=>{
        if (/^[\d,\.]+$/.test(evt)) {
            const amountValue = evt < minInputValue ? minInputValue : evt;
            updateCondition(idx, 'amount', normalizeDecimalSeparator(amountValue.toString()));
        } else {
            updateCondition(idx, 'amount', '');
        }
    };

    const handlePercentageChange = (evt, idx)=>{
        const percentageValue = evt.target.value < minInputValue ? minInputValue : (evt.target.value > 100 ? 100 : evt.target.value);
        updateCondition(idx, 'percentage', percentageValue);
    };

    const handlePerUnitChange = (evt, idx)=>{
        const perUnitValue = evt < minInputValue ? minInputValue : evt;
        updateCondition(idx, 'perUnit', perUnitValue);
    };
    const handleUnitChange = (newVal, idx)=>{
        updateCondition(idx, 'unit', newVal.value);
    };

    useEffect(() => {
        if (rowsPerPage === -1) {
            setPage(0);
        } else {
            if (conditions?.length > 0 && conditions?.length <= page * rowsPerPage) {
                setPage((current) => current - 1);
            }
        }
    }, [conditions, rowsPerPage, page]);

    useEffect(() => {
        const temp = [];
        if (rowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${conditions.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < pageSize; i++) {
                temp.push({
                    label: `${(i * rowsPerPage) + 1}-${(i + 1) * rowsPerPage < conditions.length ? (i + 1) * rowsPerPage : conditions.length}`,
                    value: i + 1,
                });
            }
        }
        return setPageNum(temp);
    }, [pageSize]);

    const handlePageSelection = (newValue) => {
        setPage(newValue);
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSameSupplierCheck = (evt) => {
        const checked = evt.target.checked;
        setUseCurrentSupplier(checked);
        setShowSelectSupplier(!checked);

        if (checked) {
            setSupplierSearchValue('');
            setSelectedSupplier({supplierCode: '', supplierName: ''});

            const tempConditions = conditions.map((condition) => ({
                ...condition,
                conditionSupplier: {...initialSupplier.current},
            }));
            setConditions(tempConditions);
        }
    };

    const handleDifferentSupllierCheck = (evt) => {
        const checked = evt.target.checked;
        setUseCurrentSupplier(!checked);
        setShowSelectSupplier(checked);
    };

    const handleClickOnTableLine = (code, name) => {
        if (code && name) {
            setSupplierSearchValue(code + ' - ' + name);
            setSelectedSupplier({supplierCode: code, supplierName: name});
            // parse all the saved conditions and add the supplier
            const tmpConditions = [];
            conditions.forEach((condition) => {
                const tmpCondition = structuredClone(condition);
                tmpCondition.conditionSupplier = {supplierCode: code, supplierName: name};
                tmpConditions.push(tmpCondition);
            });
            setConditions([...tmpConditions]);
        }
        setShowTable(false);
    };

    const handleChangeSupplierSearchType = (event) => {
        setSupplierSearchType(event.target.value);
        debouncedQuery(supplierSearchValue, event.target.value, localizedOnly);
    };

    const handleChangeSupplierSearchValue = (event) => {
        setSupplierSearchValue(event.target.value);
        debouncedQuery(event.target.value, supplierSearchType, localizedOnly);
    };

    const handleChangeLocalizedOnly = (event) => {
        setLocalizedOnly(event.target.checked);
        debouncedQuery(supplierSearchValue, supplierSearchType, event.target.checked);
    };

    const containsPossibleValue = (inputString, possibleValues) => {
        return possibleValues.some((v) => inputString?.toLowerCase().includes(v.toLowerCase()));
    };

    const getEnabledFields = (conditionType) => {
        if (!conditionType || conditionType?.label?.toLowerCase() === 'no options') {
            return {amount: false, perUnit: false, percentage: false};
        };
        const amountPossibleValues = ['val', 'fee', 'charges', 'insurance customs'];
        const quantityPossibleValues = ['quanti', 'qty', 'pallets'];
        const percentagePossibleValues = ['%'];
        // check for percentage
        const isPercentage = containsPossibleValue(conditionType?.label, percentagePossibleValues);
        const isPerUnit = containsPossibleValue(conditionType?.label, quantityPossibleValues);
        let isAmount = isPerUnit ? isPerUnit : containsPossibleValue(conditionType?.label, amountPossibleValues);

        // default case
        if (!isPercentage && !isPerUnit && !isAmount && conditionType?.label) {
            isAmount = true;
        }
        return {amount: isAmount, perUnit: isPerUnit, percentage: isPercentage};
    };

    const filteredConditionTypes = () => {
        // get all the values for the already selected conditions
        const selectedConditionsValues = [];
        conditions.forEach((cond)=>{
            selectedConditionsValues.push(cond?.conditionType?.value);
        });
        return selectedConditionsValues.length === 0 ? conditionTypes : conditionTypes.filter((cond) => !selectedConditionsValues.includes(cond.value));
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: () => {
                handleClickOnSave();
            },
            selectedConditions: conditions,
        }),
        [conditions],
    );

    const handleCurrencyChange = async (newVal, idx) => {
        // we need to extract the currency exchange
        const fetchRates = async (idx) => {
            // We send request for each different currency to get rates.
            const result = await CurrencyController.getCurrencyExchangeRate(newVal.value, companyCodeCurrency, userId, props.context);
            updateCondition(idx, 'currencyExchangeRate', result.rate);
        };
        await fetchRates(idx).catch((error) => {
            console.log(error.message);
            updateCondition(idx, 'currencyExchangeRate', 1);
        });
        updateCondition(idx, 'conditionCurrency', newVal);
    };

    const handleConditionSupplierChange = (selectedSupplier, idx) => {
        if (selectedSupplier && selectedSupplier.label) {
            const [code, ...nameParts] = selectedSupplier.label.split(' - ');
            const name = nameParts.join(' - ');
            setConditions((prevConditions) => {
                const newConditions = [...prevConditions];
                newConditions[idx] = {
                    ...newConditions[idx],
                    conditionSupplier: {supplierCode: code, supplierName: name},
                };
                return newConditions;
            });
            setSuppliersPerCondition((prev) => ({...prev, [idx]: []}));
        } else {
            setConditions((prevConditions) => {
                const newConditions = [...prevConditions];
                newConditions[idx] = {
                    ...newConditions[idx],
                    conditionSupplier: {supplierCode: '', supplierName: ''},
                };
                return newConditions;
            });
            setSuppliersPerCondition((prev) => ({...prev, [idx]: []}));
        }
    };

    const handleDeleteCondition = () => {
        if (selectedIdForDeletion.current !== null) {
            const deletedIndex = selectedIdForDeletion.current;

            const tmpArr = [...conditions];
            const deletedConditionValue = tmpArr[deletedIndex];
            tmpArr.splice(deletedIndex, 1);
            setConditions(tmpArr);

            setSuppliersPerCondition((prev) => {
                const newSuppliers = {...prev};
                delete newSuppliers[deletedIndex];

                const updatedSuppliers = {};
                Object.keys(newSuppliers).forEach((key) => {
                    const keyNum = parseInt(key, 10);
                    if (keyNum > deletedIndex) {
                        updatedSuppliers[keyNum - 1] = newSuppliers[keyNum];
                    } else {
                        updatedSuppliers[keyNum] = newSuppliers[keyNum];
                    }
                });
                return updatedSuppliers;
            });

            if (props.selectedMaterialIds.length > 1 && props.materialId !== null) {
                deleteConditionTypeForAllMaterials(deletedConditionValue.conditionType.value, props.selectedMaterialIds);
            }

            selectedIdForDeletion.current = null;
        }
        setShowDeleteConfirmation(false);
    };


    const renderTable = () =>{
        return conditions.map((condition, idx)=>{
            const enabledFields = getEnabledFields(condition?.conditionType);
            const deleteRowDisabled = isDisabled('transportCosts.deleteRow');
            const generateKey = ()=>{
                return (condition?.conditionType?.value || 'new') + '_' + idx;
            };
            return (
                <TableRow
                    key={generateKey()}
                >
                    <TableCell sx={{minWidth: 280}}>
                        <EZPRSelect
                            name={'condition'}
                            options={filteredConditionTypes()}
                            placeholder='Please select a condition type.'
                            value={condition?.conditionType?.label}
                            onChange={(evt)=>{
                                handleConditionTypeChanges(evt, idx);
                            }}
                            readOnly={isDisabled('transportCosts.conditionType')}
                        />
                    </TableCell>
                    <TableCell>
                        <TextField
                            variant={'outlined'}
                            size={'small'}
                            fullWidth
                            value={condition.percentage || ''}
                            type={'number'}
                            onChange={(evt)=>{
                                handlePercentageChange(evt, idx);
                            }}
                            onBlur={(evt)=>{
                                handlePercentageChange(evt, idx);
                            }}
                            sx={{backgroundColor: !enabledFields.percentage ? bgColorDisabled : 'unset'}}
                            disabled={!enabledFields.percentage || isDisabled('transportCosts.percentage')}
                        />
                    </TableCell>
                    <TableCell>
                        <FormatCurrencySeparatorTextfield
                            variant={'outlined'}
                            size={'small'}
                            fullWidth
                            value={condition.amount || ''}
                            type={'string'}
                            onChange={(evt)=>{
                                handleAmountChange(evt, idx);
                            }}
                            minValue={minInputValue}
                            sx={{backgroundColor: !enabledFields.amount ? bgColorDisabled : 'unset'}}
                            disabled={!enabledFields.amount || isDisabled('transportCosts.amount')}
                            separator={separator}
                            subtotalDecimals={subtotalDecimals}
                        />
                    </TableCell>
                    <TableCell>
                        <NumberTextfieldSmall
                            value={condition.perUnit|| ''}
                            onChange={(evt)=>{
                                handlePerUnitChange(evt, idx);
                            }}
                            onBlur={(evt)=>{
                                handlePerUnitChange(evt, idx);
                            }}
                            sx={{backgroundColor: !enabledFields.perUnit ? bgColorDisabled : 'unset'}}
                            disabled={!enabledFields.perUnit || isDisabled('transportCosts.perQuantity')}
                            minValue={1}
                            maxLength={5}
                        />
                    </TableCell>
                    <TableCell sx={{minWidth: 150}}>
                        <MasterDataDropdown
                            context={props.context}
                            property={'Unit of Measure'}
                            getMasterData={getMasterData}
                            materialNumber={material?.name?.split('-')?.[0]?.trim().padStart(18, '0')}
                            selectedValue={{
                                label: condition.unit || materialUnit.current,
                                value: condition.unit || materialUnit.current,
                            }}
                            userId={userId}
                            onChange={(evt)=>{
                                handleUnitChange(evt, idx);
                            }}
                            disabled={!material || enabledFields.percentage || (!enabledFields.percentage && !enabledFields.amount && !enabledFields.perUnit) || isDisabled('transportCosts.unit')}
                        />
                    </TableCell>
                    <TableCell sx={{minWidth: 150}}>
                        <MasterDataDropdown
                            property={'Currency'}
                            selectedValue={condition.conditionCurrency || materialCurrency.current}
                            context={props.context}
                            getMasterData={getMasterData}
                            onChange={(evt)=>{
                                handleCurrencyChange(evt, idx);
                            }}
                            filterQueryWithSearch
                            showValue
                            userId={userId}
                            recentlyUsed
                            disabled={!material || (!enabledFields.percentage && !enabledFields.amount && !enabledFields.perUnit) || isDisabled('transportCosts.currency')}
                        />
                    </TableCell>
                    <TableCell>
                        <TextField
                            variant={'outlined'}
                            size={'small'}
                            fullWidth
                            disabled={true}
                            sx={{backgroundColor: bgColorDisabled}}
                            value={`${formatCurrencySeparator(calculateTotalValueOfCondition(material, condition), separator, subtotalDecimals)} ${materialCurrency.current?.value}`}
                        />
                    </TableCell>
                    <TableCell sx={{minWidth: 150}}>
                        <EZPRSelect
                            name={`supplier-${idx}`}
                            options={suppliersPerCondition[idx] || []}
                            value={
                                condition.conditionSupplier &&
                                condition.conditionSupplier.supplierCode &&
                                condition.conditionSupplier.supplierName ? `${condition.conditionSupplier.supplierCode} - ${condition.conditionSupplier.supplierName}` : ''
                            }
                            placeholder='Please select a supplier'
                            onChange={(evt) => {
                                handleConditionSupplierChange(evt, idx);
                            }}
                            onInputChange={(inputValue, reason) => {
                                if (reason === 'input') {
                                    debouncedCodeOrNameQuery(inputValue, idx);
                                }
                            }}
                            tooltip={
                                condition.conditionSupplier &&
                                condition.conditionSupplier.supplierCode &&
                                condition.conditionSupplier.supplierName ? `${condition.conditionSupplier.supplierCode} - ${condition.conditionSupplier.supplierName}` : ''
                            }
                            loading={isPerformingQueryPerCondition[idx] || false}
                            minWidth={'15rem'}
                        />
                    </TableCell>
                    <TableCell>
                        {deleteRowDisabled && (<></>)}
                        {!deleteRowDisabled && (
                            <IconButton
                                component="span"
                                size={'small'}
                                onClick={()=>{
                                    handleClickOnDelete(idx);
                                }}
                                style={{color: 'red'}}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return <div>
        <TableContainer component={Paper} >
            <Table sx={{minWidth: 650}}>
                <TableHead sx={{backgroundColor: ezprOrange}}>
                    <TableRow>
                        <TableCell sx={{minWidth: 280, color: 'white'}}>Condition Type</TableCell>
                        <TableCell sx={{color: 'white'}}>Condition Percentage</TableCell>
                        <TableCell sx={{color: 'white'}}>Condition Amount</TableCell>
                        <TableCell sx={{color: 'white'}}>Condition Per Quantity</TableCell>
                        <TableCell sx={{minWidth: 150, color: 'white'}}>Condition Unit</TableCell>
                        <TableCell sx={{minWidth: 150, color: 'white'}}>Condition Currency</TableCell>
                        <TableCell sx={{color: 'white'}}>Condition Total Value</TableCell>
                        <TableCell sx={{minWidth: 150, color: 'white'}}>Supplier</TableCell>
                        <TableCell colSpan={1}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={10} style={{textAlign: 'center'}}>
                                <CircularProgress/>
                            </TableCell>
                        </TableRow>
                    )}
                    {(conditions.length > 0 && !loading) && renderTable()}
                    {(!loading && conditions.length === 0 ) && (
                        <TableRow>
                            <TableCell colSpan={10} style={{textAlign: 'center'}}>
                                    No data exists
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell colSpan={10} style={{textAlign: 'center'}}>
                            <div className={'pr-editor-account-details-account-action-container'}>
                                <Button
                                    className="pr-editor-account-details-split-btn"
                                    startIcon={<AddIcon/>}
                                    sx={{
                                        color: ezprOrange,
                                        borderColor: ezprOrange,
                                        border: '2px solid',
                                        borderRadius: '20px',
                                        marginTop: '0.5rem',
                                        position: 'absolute',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                    }}
                                    onClick={() => {
                                        const newIndex = conditions.length;
                                        const tmpConditions = [...conditions];
                                        tmpConditions.push({
                                            'conditionType': null,
                                            'amount': '',
                                            'percentage': '',
                                            'perUnit': '',
                                            'conditionSupplier': selectedSupplier || initialSupplier.current || {supplierCode: vendorCode, supplierName: vendorName},
                                            'conditionCurrency': materialCurrency.current,
                                            'unit': materialUnit.current,
                                            'eprId': EPRId,
                                            'currencyExchangeRate': 1,
                                        });
                                        setConditions(tmpConditions);
                                        setSuppliersPerCondition((prev) => ({...prev, [newIndex]: []}));
                                    }}
                                    endIcon={<></>}
                                    disabled={loading || isDisabled('transportCosts.addNew')}
                                >Add new</Button>
                                <TablePaginationWithPageSelector
                                    page={page}
                                    pageNum={pageNum}
                                    count={conditions.length}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[2, 5, 10, 20, 50, 100, {label: 'All', value: -1}]}
                                    handlePageSelection={handlePageSelection}
                                    handleChangePage={handleChangePage}
                                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                                />
                            </div>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
        <FormGroup >
            <div style={{display: 'flex', marginTop: '1rem'}}>
                <FormControlLabel
                    control={<Checkbox checked={useCurrentSupplier} onChange={handleSameSupplierCheck} disabled={isDisabled('transportCosts.currentSupplier')}/>}
                    label={`Supplier: ${initialSupplier.current.supplierCode} - ${initialSupplier.current.supplierName}`}/>
                <FormControlLabel control={<Checkbox checked={!useCurrentSupplier}
                    disabled={isDisabled('transportCosts.otherSupplier')}
                    onChange={handleDifferentSupllierCheck}/>}
                label={'Other supplier'}/>
            </div>
        </FormGroup>
        {showSelectSupplier && (
            <div style={{paddingTop: '1rem'}}>
                <div>Please define the supplier</div>
                <div className={'popup-dyn-search-supplier-popup-textfield-and-select'}>
                    <TextField
                        className={'popup-dyn-search-supplier-popup-textfield'}
                        placeholder="Search Company"
                        variant="outlined"
                        value={supplierSearchValue}
                        onChange={handleChangeSupplierSearchValue}
                        InputProps={{
                            endAdornment:
                        <InputAdornment position={'end'}>
                            {isPerformingQuery && <CircularProgress/>}
                        </InputAdornment>,
                        }}/>
                    <FormControl className={'popup-dyn-search-supplier-popup-select'}>
                        <Select
                            placeholder={'By ...'}
                            value={supplierSearchType}
                            onChange={handleChangeSupplierSearchType}
                        >
                            <MenuItem value={'lifnr'}>By Code</MenuItem>
                            <MenuItem value={'name1'}>By Name</MenuItem>
                            <MenuItem value={'ort01'}>By Region</MenuItem>
                            <MenuItem value={'land1'}>By Country</MenuItem>
                            <MenuItem value={'ekorg'}>By Purchase Org</MenuItem>
                            <MenuItem value={'bukrs'}>By Company Code</MenuItem>
                            <MenuItem value={'ktokk'}>By Account Group</MenuItem>
                            <MenuItem value={'stceg'}>By VAT (tax ID)</MenuItem>
                        </Select>
                    </FormControl>
                </div>
                {showTable && <SuppliersTable
                    context={props.context}
                    suppliers={suppliers}
                    handleClick={handleClickOnTableLine}
                    companyCode={companyCode}
                    purchaseOrg={purchaseOrg}
                    localizedOnly={localizedOnly}
                    count={numberOfResults}
                />
                }
                <FormControlLabel
                    control={<Checkbox checked={localizedOnly} onChange={handleChangeLocalizedOnly}/>}
                    label={'Show localized suppliers only'}/>
            </div>)}
        {showDeleteConfirmation && (
            <CustomPopup
                buttons={[
                    <Button
                        key={1}
                        onClick={() => {
                            selectedIdForDeletion.current = null;
                            setShowDeleteConfirmation(false);
                        }}
                        autoFocus
                    >
                         Cancel
                    </Button>,
                    <Button
                        key={2}
                        onClick={handleDeleteCondition}
                    >
                        Delete
                    </Button>,
                ]}
                open={showDeleteConfirmation}
                handleClose={() => setShowDeleteConfirmation(false)}
            />
        )}
    </div>;
});

ConditionTypeContent.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    selectedMaterialIds: PropTypes.array,
    materialId: PropTypes.number,
};

